import ApiBase from './ApiBase';
import axios from 'axios';

/**
 * User API
 */
class UserApi extends ApiBase {
  /**
   * Reset user password
   * @param {*} userId
   */
  async resetPassword(userId) {
    const body = {
      userId: userId
    };
    const request = {
      url: `${this.API_URL}/user/resetpassword`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(body)
    };

    await axios.request(request);
  }

  /**
   * create userauthorizations
   * @param {*} userauthorizations
   */
  async createUserauthorizations(userauthorizations) {
    const request = {
      url: `${this.API_URL}/userauthorizations`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(userauthorizations)
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * delete userauthorizations
   * @param {*} userauthorizations
   */
  async deleteUserauthorizations(userauthorizations) {
    const request = {
      url: `${this.API_URL}/userauthorizations`,
      headers: this.getHeaders(),
      method: 'DELETE',
      data: JSON.stringify(userauthorizations)
    };

    return axios.request(request);
  }

  /**
   *  post sendwelcomeemail
   * @param {*} id
   */
  async sendWelcomeEmail(id) {
    const request = {
      url: `${this.API_URL}/user/sendwelcomeemail?id=${id}`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(id)
    };

    return axios.request(request);
  }

  /**
   * Reset user mfa
   * @param {*} userId
   */
  async resetMfa(userId) {
    const request = {
      url: `${this.API_URL}/user/mfa/resetMfaAndSendEnrollment?id=${userId}`,
      headers: this.getHeaders(),
      method: 'POST'
    };

    return axios.request(request);
  }

  /**
   * Get users mfa information
   * @param {*} userId
   */
  async getUserMFA(userId) {
    const request = {
      url: `${this.API_URL}/users/mfa?userId=${userId}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set users mfa information
   * @param userMFA
   */
  async setUserMFA(userMFA) {
    const request = {
      url: `${this.API_URL}/users/mfa`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(userMFA)
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Returns array of matching emails
   * @param {string} email
   */
  async checkIfEmailExists(email) {
    const request = {
      url: `${this.API_URL}/users?email=${email}&orphanVisibility=ALL_VISIBLE`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set custom temp password to user
   * @param {*} userId
   * @param {*} password
   */
  async setPassword(userId, password) {
    const body = {
      userIdToUpdate: userId,
      newPassword: password
    };
    const request = {
      url: `${this.API_URL}/user/setpassword`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(body)
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Get user available sso authentications modes
   */
  async getSSOAuthenticationsModes() {
    const request = {
      url: `${this.API_URL}/user/sso/authenticationModes`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Get users SSO authentication mode
   * @param {*} userId
   */
  async getUserSSO(userId) {
    const request = {
      url: `${this.API_URL}/user/sso/authenticationMode?id=${userId}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Set users SSO authentication mode
   * @param authMethod
   */
  async setUserSSO(authMethod) {
    const request = {
      url: `${this.API_URL}/user/sso/authenticationMode`,
      headers: this.getHeaders(),
      method: 'PUT',
      data: JSON.stringify(authMethod)
    };

    const response = await axios.request(request);

    return response.data;
  }

  async validateImportFromExcel(payload) {
    const request = {
      url: `${this.API_URL}/user/validateimport`,
      method: 'POST',
      headers: this.getHeaders(),
      data: payload
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response.data;
  }

  async importUsersFromExcel(payload) {
    const request = {
      url: `${this.API_URL}/users/import`,
      method: 'POST',
      headers: this.getHeaders(),
      data: payload
    };

    const response = await axios.request(request);

    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response.data;
  }

  /**
   * Get contact template excel
   */
  async getContactExcelTemplate() {
    const request = {
      url: `${this.API_URL}/user/import/template`,
      headers: this.getHeaders(),
      method: 'GET',
      responseType: 'blob'
    };

    const response = await axios.request(request);

    return response.data;
  }

  async exportUsersExcel(
    name,
    fundFamillyId,
    id,
    mupUserId,
    email,
    status,
    orphanVisibility,
    selectedIds
  ) {
    let payload;
    if (selectedIds && selectedIds.length > 0) {
      payload = {
        userId: `[${selectedIds.join(',')}]`,
        orphanVisibility: 'ALL_VISIBLE'
      };
    } else {
      payload = {
        userId: id,
        mupUserId: mupUserId,
        name: name,
        fundFamilyId: fundFamillyId,
        email: email,
        status: status,
        orphanVisibility: 'ALL_VISIBLE'
      };
    }

    const request = {
      url: `${this.API_URL}/users/excel/export`,
      method: 'POST',
      headers: this.getHeaders(),
      responseType: 'blob',
      data: payload
    };

    const response = await axios.request(request);
    
    // Handle Gateway Timeout
    if (response.status === 504) {
      throw new Error('The request took too long and timed out.');
    }

    return response.data;
  }

  /**
   * Update an existing user
   */
  async updateUser(id, payload) {
    const request = {
      url: `${this.API_URL}/user?id=${id}`,
      headers: this.getHeaders(),
      method: 'PUT',
      data: payload
    };

    const response = await axios.request(request);

    return response.data;
  }
}

export default new UserApi();
